import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import classNames from 'classnames'

import styles from './DashboardTooltip.module.scss'

const GAP = 8
const VIEWPORT_MARGIN = 8
// Keep the arrow on the flat part of the pill, away from its rounded ends
// (border-radius: 999px). 12px ≈ corner radius (~11px) + half the arrow.
const ARROW_EDGE_MARGIN = 12
const NO_SHIFT = { x: 0, y: 0, arrowX: 0, arrowY: 0 }

const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value))

const getCoords = (rect, placement) => {
  switch (placement) {
    case 'bottom':
      return {
        left: rect.left + rect.width / 2,
        top: rect.bottom + GAP,
        transform: 'translate(-50%, 0)'
      }
    case 'left':
      return {
        left: rect.left - GAP,
        top: rect.top + rect.height / 2,
        transform: 'translate(-100%, -50%)'
      }
    case 'right':
      return {
        left: rect.right + GAP,
        top: rect.top + rect.height / 2,
        transform: 'translate(0, -50%)'
      }
    case 'top':
    default:
      return {
        left: rect.left + rect.width / 2,
        top: rect.top - GAP,
        transform: 'translate(-50%, -100%)'
      }
  }
}

const DashboardTooltip = ({
  children,
  className,
  delay = 0,
  disabled = false,
  label,
  placement = 'top',
  shouldShow
}) => {
  const rootRef = useRef(null)
  const bubbleRef = useRef(null)
  const timerRef = useRef(null)
  const [coords, setCoords] = useState(null)
  const [shift, setShift] = useState(NO_SHIFT)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const show = useCallback(() => {
    const reveal = () => {
      const el = rootRef.current
      if (!el) return

      // Optional check that runs on hover (e.g. "only show if the text is
      // truncated"), so consumers don't have to measure layout on mount.
      if (shouldShow && !shouldShow()) return

      setShift(NO_SHIFT)
      setCoords(getCoords(el.getBoundingClientRect(), placement))
    }

    if (delay > 0) {
      clearTimer()
      timerRef.current = setTimeout(reveal, delay)

      return
    }

    reveal()
  }, [clearTimer, delay, placement, shouldShow])

  const hide = useCallback(() => {
    clearTimer()
    setCoords(null)
    setShift(NO_SHIFT)
  }, [clearTimer])

  // Keep the bubble inside the viewport. The bubble is measured once each
  // time it opens, and moved back on screen in a single step.
  //
  // `reveal` resets shift before setting coords, so the rect measured here is
  // always the bubble at its original position. Do not add `shift` to the
  // dependencies or accumulate deltas: that re-runs the effect after every
  // correction, and when the bubble is bigger than the viewport (or the
  // browser returns fractional pixels, e.g. with zoom) the correction never
  // reaches zero. React then throws error #185 (maximum update depth
  // exceeded).
  useLayoutEffect(() => {
    if (!coords || !bubbleRef.current) return

    const rect = bubbleRef.current.getBoundingClientRect()

    let deltaX = 0
    if (rect.left < VIEWPORT_MARGIN) {
      deltaX = VIEWPORT_MARGIN - rect.left
    } else if (rect.right > window.innerWidth - VIEWPORT_MARGIN) {
      deltaX = window.innerWidth - VIEWPORT_MARGIN - rect.right
    }

    let deltaY = 0
    if (rect.top < VIEWPORT_MARGIN) {
      deltaY = VIEWPORT_MARGIN - rect.top
    } else if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN) {
      deltaY = window.innerHeight - VIEWPORT_MARGIN - rect.bottom
    }

    // When the bubble is pushed back on screen, the arrow must move the
    // opposite way so it keeps pointing at the anchor (which has not moved).
    // Clamp it to the bubble's flat region so it never falls off the rounded
    // ends. Top/bottom arrows track the horizontal shift, left/right the
    // vertical one.
    let arrowX = 0
    let arrowY = 0
    if (placement === 'top' || placement === 'bottom') {
      arrowX = clamp(-deltaX, Math.max(0, rect.width / 2 - ARROW_EDGE_MARGIN))
    } else {
      arrowY = clamp(-deltaY, Math.max(0, rect.height / 2 - ARROW_EDGE_MARGIN))
    }

    if (deltaX !== 0 || deltaY !== 0) {
      setShift({ x: deltaX, y: deltaY, arrowX, arrowY })
    }
  }, [coords, placement])

  useEffect(() => clearTimer, [clearTimer])

  if (disabled || !label) {
    return children
  }

  return (
    <span
      ref={rootRef}
      className={classNames(styles.root, className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {coords &&
        createPortal(
          <span
            ref={bubbleRef}
            className={classNames(
              styles.bubble,
              styles[`bubble--${placement}`],
              styles['bubble--arrow']
            )}
            style={{
              left: coords.left + shift.x,
              top: coords.top + shift.y,
              transform: coords.transform,
              '--arrow-x': `${shift.arrowX}px`,
              '--arrow-y': `${shift.arrowY}px`
            }}
          >
            {label}
          </span>,
          document.body
        )}
    </span>
  )
}

export default DashboardTooltip
